import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, List, ListItem, Chip, Slider } from '@mui/material';
import { MenuBook } from '@mui/icons-material';
import { BEHAVIORAL_CODES } from './studentLabelingConfig';

export default function StudentLabelingDialogs({
  codebookOpen,
  setCodebookOpen,
  stopRecordingOpen,
  activeRecording,
  stopRecordingEndTime,
  stopRecordingConfidence,
  setStopRecordingConfidence,
  handleStopRecordingConfirm,
  handleStopRecordingCancel,
  formatTime,
  editDialogOpen,
  editingBlock,
  setEditingBlock,
  handleEditConfirm,
  handleEditCancel,
  duration,
}) {
  const [thinkingSubcategory, setThinkingSubcategory] = useState(null);

  const thinkingBehavior = BEHAVIORAL_CODES.find(b => b.id === 'thinking');
  const isThinkingBehavior = activeRecording?.behavior?.id === 'thinking';

  const handleCancel = () => {
    setThinkingSubcategory(null);
    handleStopRecordingCancel();
  };

  const handleConfirm = () => {
    handleStopRecordingConfirm(thinkingSubcategory);
    setThinkingSubcategory(null);
  };

  const isSaveDisabled = !stopRecordingConfidence || (isThinkingBehavior && !thinkingSubcategory);

  // Edit dialog helpers
  const handleStartTimeChange = (_, value) => {
    if (!editingBlock) return;
    const newStart = Math.min(value, editingBlock.endTime - 1000); // At least 1 second duration
    setEditingBlock({ ...editingBlock, startTime: newStart });
  };

  const handleEndTimeChange = (_, value) => {
    if (!editingBlock) return;
    const newEnd = Math.max(value, editingBlock.startTime + 1000); // At least 1 second duration
    setEditingBlock({ ...editingBlock, endTime: newEnd });
  };

  const isEditingThinking = editingBlock?.behavior?.id === 'thinking';

  return (
    <>
      {/* Codebook Modal */}
      <Dialog open={codebookOpen} onClose={() => setCodebookOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
          Behavioral Labeling Tutorial
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 2, bgcolor: '#000' }}>
            <video
              src="https://vizpi-labeling-tutorial.s3.us-east-2.amazonaws.com/Safari.mp4"
              controls
              autoPlay
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '8px' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodebookOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Stop Recording Dialog */}
      <Dialog 
        open={stopRecordingOpen} 
        onClose={handleCancel}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 340 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeRecording?.behavior && (
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: activeRecording.behavior.color }} />
            )}
            <Typography variant="h6">Save Tag</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {activeRecording && (
            <>
              <Box sx={{ p: 1.5, bgcolor: '#f9fafb', borderRadius: 2, mb: 2.5 }}>
                <Typography variant="body2" fontWeight="600" sx={{ color: '#374151' }}>
                  {activeRecording.behavior?.label}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  {formatTime(activeRecording.startTime)} → {formatTime(stopRecordingEndTime)}
                </Typography>
              </Box>

              {/* Thinking Subcategory Selection */}
              {isThinkingBehavior && thinkingBehavior?.subcategories && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                    What were you thinking about?
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2.5 }}>
                    {thinkingBehavior.subcategories.map((sub) => (
                      <Chip
                        key={sub.id}
                        label={sub.label}
                        onClick={() => setThinkingSubcategory(sub.id)}
                        variant={thinkingSubcategory === sub.id ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          borderColor: thinkingSubcategory === sub.id ? '#3b82f6' : '#e5e7eb',
                          bgcolor: thinkingSubcategory === sub.id ? '#3b82f6' : 'transparent',
                          color: thinkingSubcategory === sub.id ? '#fff' : '#4b5563',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            bgcolor: thinkingSubcategory === sub.id ? '#2563eb' : '#f8fafc',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </>
              )}
              
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                How confident are you in this label?
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[1, 2, 3, 4, 5].map((v) => (
                  <Box
                    key={v}
                    onClick={() => setStopRecordingConfidence(v)}
                    sx={{
                      flex: 1,
                      height: 40,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: stopRecordingConfidence === v ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      bgcolor: stopRecordingConfidence === v ? '#eff6ff' : '#fff',
                      color: stopRecordingConfidence === v ? '#1d4ed8' : '#9ca3af',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#3b82f6', bgcolor: '#f8fafc' }
                    }}
                  >
                    {v}
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>Guessing</Typography>
                <Typography variant="caption" sx={{ color: '#3b82f6', fontSize: '0.65rem' }}>Certain</Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancel} sx={{ color: '#6b7280' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            variant="contained"
            disabled={isSaveDisabled}
            sx={{ 
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
              '&:disabled': { bgcolor: '#e5e7eb' }
            }}
          >
            Save Tag
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tag Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleEditCancel}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 380 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {editingBlock?.behavior && (
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: editingBlock.behavior.color }} />
            )}
            <Typography variant="h6">Edit Tag</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingBlock && (
            <>
              <Box sx={{ p: 1.5, bgcolor: '#f9fafb', borderRadius: 2, mb: 3 }}>
                <Typography variant="body2" fontWeight="600" sx={{ color: '#374151' }}>
                  {editingBlock.behavior?.label}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  {formatTime(editingBlock.startTime)} → {formatTime(editingBlock.endTime)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>
                  Duration: {formatTime(editingBlock.endTime - editingBlock.startTime)}
                </Typography>
              </Box>

              {/* Start Time Slider */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                  Start Time: {formatTime(editingBlock.startTime)}
                </Typography>
                <Slider
                  value={editingBlock.startTime}
                  onChange={handleStartTimeChange}
                  min={0}
                  max={duration}
                  step={1000}
                  sx={{
                    color: editingBlock.behavior?.color || '#3b82f6',
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                    },
                  }}
                />
              </Box>

              {/* End Time Slider */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                  End Time: {formatTime(editingBlock.endTime)}
                </Typography>
                <Slider
                  value={editingBlock.endTime}
                  onChange={handleEndTimeChange}
                  min={0}
                  max={duration}
                  step={1000}
                  sx={{
                    color: editingBlock.behavior?.color || '#3b82f6',
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                    },
                  }}
                />
              </Box>

              {/* Thinking Subcategory (if applicable) */}
              {isEditingThinking && thinkingBehavior?.subcategories && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                    What were you thinking about?
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {thinkingBehavior.subcategories.map((sub) => (
                      <Chip
                        key={sub.id}
                        label={sub.label}
                        onClick={() => setEditingBlock({ ...editingBlock, thinkingSubcategory: sub.id })}
                        variant={editingBlock.thinkingSubcategory === sub.id ? 'filled' : 'outlined'}
                        sx={{
                          cursor: 'pointer',
                          borderColor: editingBlock.thinkingSubcategory === sub.id ? '#3b82f6' : '#e5e7eb',
                          bgcolor: editingBlock.thinkingSubcategory === sub.id ? '#3b82f6' : 'transparent',
                          color: editingBlock.thinkingSubcategory === sub.id ? '#fff' : '#4b5563',
                          '&:hover': {
                            borderColor: '#3b82f6',
                            bgcolor: editingBlock.thinkingSubcategory === sub.id ? '#2563eb' : '#f8fafc',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Confidence */}
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
                Confidence: {editingBlock.confidence}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[1, 2, 3, 4, 5].map((v) => (
                  <Box
                    key={v}
                    onClick={() => setEditingBlock({ ...editingBlock, confidence: v })}
                    sx={{
                      flex: 1,
                      height: 40,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: editingBlock.confidence === v ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      bgcolor: editingBlock.confidence === v ? '#eff6ff' : '#fff',
                      color: editingBlock.confidence === v ? '#1d4ed8' : '#9ca3af',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#3b82f6', bgcolor: '#f8fafc' }
                    }}
                  >
                    {v}
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>Guessing</Typography>
                <Typography variant="caption" sx={{ color: '#3b82f6', fontSize: '0.65rem' }}>Certain</Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleEditCancel} sx={{ color: '#6b7280' }}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleEditConfirm(editingBlock)} 
            variant="contained"
            disabled={!editingBlock || editingBlock.startTime >= editingBlock.endTime}
            sx={{ 
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' },
              '&:disabled': { bgcolor: '#e5e7eb' }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}